import React, { useState, useMemo } from 'react';
import { 
  Building2, 
  FlaskConical, 
  Scissors,
  CalendarDays,
  ChevronDown,
  ChevronUp,
  Award
} from 'lucide-react';
import { useClientPOs } from '@/hooks/useClientPOs';
import { Input } from '@/components/ui/input';
import CustomDatePicker from '@/components/shared/CustomDatePicker';

export default function FinanceOmset() {
  const { clientPOs, isLoading } = useClientPOs();
  const [periode, setPeriode] = useState('Semua');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');

  const [expandedPO, setExpandedPO] = useState(false);
  const [expandedCustom, setExpandedCustom] = useState(false);
  const [expandedSample, setExpandedSample] = useState(false);

  // Fungsi utilitas untuk parsing tanggal dari string invoice (diambil dari logika FinanceInvoices)
  const parseInvoiceDate = (dStr) => {
    if (!dStr) return 0;
    let d = new Date(dStr);
    if (isNaN(d.getTime()) && typeof dStr === 'string') {
      const str = dStr.toLowerCase();
      const months = { 'jan':0,'feb':1,'mar':2,'apr':3,'mei':4,'may':4,'jun':5,'jul':6,'agu':7,'aug':7,'sep':8,'okt':9,'oct':9,'nov':10,'des':11,'dec':11 };
      const parts = str.split(' ');
      if (parts.length >= 3) {
         const day = parseInt(parts[0]);
         const monthStr = Object.keys(months).find(m => parts[1].includes(m));
         const month = monthStr ? months[monthStr] : 0;
         const year = parseInt(parts[2]);
         d = new Date(year, month, day);
      }
    }
    return isNaN(d.getTime()) ? 0 : d.getTime();
  };

  const isWithinPeriod = (timestamp) => {
    if (periode === 'Semua') return true;
    if (timestamp === 0) return false;
    
    const date = new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if (periode === 'Hari ini') {
      const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      return d.getTime() === today.getTime();
    }
    if (periode === '7 Hari') {
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays <= 7;
    }
    if (periode === '30 Hari') {
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays <= 30;
    }
    if (periode === 'Bulan Ini') {
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    }
    if (periode === 'Custom') {
      let match = true;
      const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      if (customDateFrom) {
        const from = new Date(customDateFrom);
        if (d < from) match = false;
      }
      if (customDateTo) {
        const to = new Date(customDateTo);
        if (d > to) match = false;
      }
      return match;
    }
    return true;
  };

  const processedData = useMemo(() => {
    let listPO = [];
    let listCustom = [];
    let listSample = [];

    clientPOs.forEach(po => {
      if (po.poMode === 'sph') return;
      // 1. Tentukan tanggal untuk filter periode
      const isSelesai = po.statusProduksi === 'PESANAN SELESAI' || po.statusProduksi === 'SELESAI';
      const dStr = (isSelesai && (po.updated_at || po.rawRecord?.updated_at)) ? (po.updated_at || po.rawRecord.updated_at) : (po.date || po.tanggal || po.created_date || po.created_at || '');
      const timestamp = parseInvoiceDate(dStr);
      
      if (!isWithinPeriod(timestamp)) return;

      // 2. Hitung total nilai omset (Total Harga Jual)
      const prodJual = (po.products || po.items || []).reduce((s, p) => s + (p.total_harga_jual || p.subtotal || p.hargaTotal || 0), 0);
      const legJual = (po.legalitas || []).reduce((s, l) => s + (l.harga_jual * (l.qty_variant || 1)), 0);
      const shipping = po.shippingCost || 0;
      const poJasa = po.biaya_desain || po.jasa_desain || 0;
      
      const totalOmset = prodJual + legJual + shipping + poJasa;

      // 3. Tentukan kategori
      const isCustom = po.poMode === 'custom_finance' || po.is_custom_finance;
      const isSample = po.poMode === 'sample' || po.isSample === true;

      const brandName = (po.customerName || po.client || 'Client Kustom').split(' / ')[0].trim();
      
      const record = { brandName, totalOmset };

      if (isCustom) {
        listCustom.push(record);
      } else if (isSample) {
        listSample.push(record);
      } else {
        listPO.push(record);
      }
    });

    const aggregateRanking = (list) => {
      const map = {};
      list.forEach(item => {
        if (!map[item.brandName]) map[item.brandName] = 0;
        map[item.brandName] += item.totalOmset;
      });
      return Object.entries(map)
        .map(([brand, omset]) => ({ brand, omset }))
        .sort((a, b) => b.omset - a.omset);
    };

    const rankPO = aggregateRanking(listPO);
    const rankCustom = aggregateRanking(listCustom);
    const rankSample = aggregateRanking(listSample);

    const sumPO = rankPO.reduce((s, r) => s + r.omset, 0);
    const sumCustom = rankCustom.reduce((s, r) => s + r.omset, 0);
    const sumSample = rankSample.reduce((s, r) => s + r.omset, 0);

    return { rankPO, rankCustom, rankSample, sumPO, sumCustom, sumSample };
  }, [clientPOs, periode, customDateFrom, customDateTo]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const renderRanking = (rankingList) => {
    if (rankingList.length === 0) {
      return <div className="text-sm text-gray-500 text-center py-4 italic">Belum ada data omset di periode ini.</div>;
    }
    return (
      <div className="mt-4 space-y-2">
        {rankingList.map((item, idx) => (
          <div key={idx} className="flex justify-between items-center p-2 rounded-lg bg-gray-50 border border-gray-100 hover:bg-gray-100 transition-colors">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-[#F8F5F1] text-[#A39171] flex items-center justify-center text-xs font-bold border border-[#E6D5B8]">
                {idx + 1}
              </div>
              <p className="text-sm font-semibold text-[#302621]">{item.brand}</p>
            </div>
            <p className="text-sm font-bold text-[#302621]">{formatCurrency(item.omset)}</p>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-[#302621] tracking-tight">Omset</h1>
          <p className="text-sm text-[#7A695B] mt-1">Ringkasan omset dari seluruh invoice berdasarkan kategori</p>
        </div>
      </div>

      {/* Filter Periode */}
      <div className="bg-white p-3 md:p-4 rounded-xl border border-[#E6D5B8]/50 shadow-sm flex flex-col md:flex-row gap-4 items-start md:items-center">
        <div className="flex items-center gap-2 text-[#7A695B] font-semibold text-sm mr-2 shrink-0">
          <CalendarDays className="w-4 h-4" />
          <span>PERIODE:</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {['Hari ini', '7 Hari', '30 Hari', 'Bulan Ini', 'Semua', 'Custom'].map(p => (
            <button
              key={p}
              onClick={() => setPeriode(p)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                periode === p 
                ? 'bg-[#302621] text-white shadow-md' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
        
        {periode === 'Custom' && (
          <div className="flex items-center gap-2 mt-2 md:mt-0 animate-in fade-in slide-in-from-left-2">
            <CustomDatePicker 
              value={customDateFrom} 
              onChange={setCustomDateFrom}
              className="text-xs h-8 w-32"
            />
            <span className="text-xs text-gray-500">-</span>
            <CustomDatePicker 
              value={customDateTo} 
              onChange={setCustomDateTo}
              className="text-xs h-8 w-32"
            />
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-[#E6D5B8]/50 shadow-sm relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-40 h-40 bg-[#F9F5EC] rounded-bl-full -mr-20 -mt-20 opacity-50 transition-transform group-hover:scale-110 -z-10"></div>
        <div className="p-5 md:p-6 relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <p className="text-sm font-bold text-[#A39171] uppercase tracking-wider mb-1">Total Omset Keseluruhan</p>
              <h3 className="text-3xl md:text-5xl font-black text-[#302621]">
                {formatCurrency(processedData.sumPO + processedData.sumCustom + processedData.sumSample)}
              </h3>
            </div>
            <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-[#F9F5EC] border border-[#E6D5B8]/50 flex items-center justify-center text-[#8C6208] shrink-0 shadow-sm">
              <Award className="w-7 h-7 md:w-8 md:h-8" />
            </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        {/* Card Omset PO */}
        <div className="bg-white rounded-2xl border border-[#E6D5B8]/50 shadow-sm relative overflow-hidden group">
          <div className="p-5 md:p-6 relative z-10">
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full -mr-16 -mt-16 opacity-50 transition-transform group-hover:scale-110 -z-10"></div>
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-bold text-[#A39171] uppercase tracking-wider mb-1">Omset PO</p>
                <h3 className="text-2xl md:text-3xl font-black text-[#302621]">
                  {formatCurrency(processedData.sumPO)}
                </h3>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600">
                <Building2 className="w-6 h-6" />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <button 
                onClick={() => setExpandedPO(!expandedPO)}
                className="w-full flex items-center justify-center gap-2 text-xs font-bold text-gray-500 hover:text-[#302621] transition-colors"
              >
                <Award className="w-4 h-4" /> 
                {expandedPO ? 'TUTUP RANKING' : 'LIHAT RANKING'}
                {expandedPO ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>}
              </button>
              {expandedPO && (
                <div className="animate-in fade-in slide-in-from-top-2">
                  {renderRanking(processedData.rankPO)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Card Omset Custom */}
        <div className="bg-white rounded-2xl border border-[#E6D5B8]/50 shadow-sm relative overflow-hidden group">
          <div className="p-5 md:p-6 relative z-10">
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50 rounded-bl-full -mr-16 -mt-16 opacity-50 transition-transform group-hover:scale-110 -z-10"></div>
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-bold text-[#A39171] uppercase tracking-wider mb-1">Omset Custom</p>
                <h3 className="text-2xl md:text-3xl font-black text-[#302621]">
                  {formatCurrency(processedData.sumCustom)}
                </h3>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-600">
                <Scissors className="w-6 h-6" />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <button 
                onClick={() => setExpandedCustom(!expandedCustom)}
                className="w-full flex items-center justify-center gap-2 text-xs font-bold text-gray-500 hover:text-[#302621] transition-colors"
              >
                <Award className="w-4 h-4" /> 
                {expandedCustom ? 'TUTUP RANKING' : 'LIHAT RANKING'}
                {expandedCustom ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>}
              </button>
              {expandedCustom && (
                <div className="animate-in fade-in slide-in-from-top-2">
                  {renderRanking(processedData.rankCustom)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Card Omset Sample */}
        <div className="bg-white rounded-2xl border border-[#E6D5B8]/50 shadow-sm relative overflow-hidden group">
          <div className="p-5 md:p-6 relative z-10">
            <div className="absolute top-0 right-0 w-32 h-32 bg-orange-50 rounded-bl-full -mr-16 -mt-16 opacity-50 transition-transform group-hover:scale-110 -z-10"></div>
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-bold text-[#A39171] uppercase tracking-wider mb-1">Omset Sample</p>
                <h3 className="text-2xl md:text-3xl font-black text-[#302621]">
                  {formatCurrency(processedData.sumSample)}
                </h3>
              </div>
              <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center text-orange-600">
                <FlaskConical className="w-6 h-6" />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <button 
                onClick={() => setExpandedSample(!expandedSample)}
                className="w-full flex items-center justify-center gap-2 text-xs font-bold text-gray-500 hover:text-[#302621] transition-colors"
              >
                <Award className="w-4 h-4" /> 
                {expandedSample ? 'TUTUP RANKING' : 'LIHAT RANKING'}
                {expandedSample ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>}
              </button>
              {expandedSample && (
                <div className="animate-in fade-in slide-in-from-top-2">
                  {renderRanking(processedData.rankSample)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
